import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Users, User, ArrowRightLeft, Upload, FileText } from 'lucide-react';
import { MatchplayFormat, CreatePlayerRequest } from '../../../types/matchplay-v2';

interface PlayerPairingProps {
  tournamentId: string;
  format: MatchplayFormat;
  onPlayersSetup: (players: CreatePlayerRequest[]) => void;
  isLoading: boolean;
}

interface PlayerInput extends CreatePlayerRequest {
  id: string; // Temporary ID for form management
}

export function PlayerPairing({ tournamentId, format, onPlayersSetup, isLoading }: PlayerPairingProps) {
  const [players, setPlayers] = useState<PlayerInput[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize with empty players based on format
  useEffect(() => {
    const minPlayers = format === 'singles' ? 2 : 4;
    if (players.length === 0) {
      setPlayers(Array.from({ length: minPlayers }, (_, i) => ({
        id: `player-${i}`,
        playerName: '',
        handicap: 0,
        teamAssignment: format === 'singles' ? undefined : i < minPlayers / 2 ? 'A' : 'B'
      })));
    }
  }, [format]);

  const addPlayer = () => {
    const newPlayer: PlayerInput = {
      id: `player-${Date.now()}`,
      playerName: '',
      handicap: 0,
      teamAssignment: format === 'singles' ? undefined : 'A'
    };
    setPlayers([...players, newPlayer]);
  };

  const removePlayer = (playerId: string) => {
    setPlayers(players.filter(p => p.id !== playerId));
    // Remove any errors for this player
    const newErrors = { ...errors };
    delete newErrors[playerId];
    setErrors(newErrors);
  };

  const updatePlayer = (playerId: string, field: keyof PlayerInput, value: any) => {
    setPlayers(players.map(p => 
      p.id === playerId ? { ...p, [field]: value } : p
    ));
    
    // Clear error for this field
    const errorKey = `${playerId}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const validatePlayers = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Check each player
    players.forEach(player => {
      if (!player.playerName.trim()) {
        newErrors[`${player.id}-playerName`] = 'Player name is required';
      }
      
      if (player.handicap < 0 || player.handicap > 54) {
        newErrors[`${player.id}-handicap`] = 'Handicap must be between 0 and 54';
      }
    });

    // Check minimum players
    const minPlayers = format === 'singles' ? 2 : 4;
    if (players.length < minPlayers) {
      newErrors.general = `At least ${minPlayers} players required for ${format}`;
    }

    // Check team balance for team formats
    if (format !== 'singles') {
      const teamA = players.filter(p => p.teamAssignment === 'A');
      const teamB = players.filter(p => p.teamAssignment === 'B');
      
      if (teamA.length !== teamB.length) {
        newErrors.general = 'Teams must have equal number of players';
      }
      
      if (teamA.length % 2 !== 0) {
        newErrors.general = `Each team must have an even number of players for ${format}`;
      }
    }

    // Check for duplicate names
    const names = players.map(p => p.playerName.trim().toLowerCase());
    const duplicateNames = names.filter((name, index) => names.indexOf(name) !== index);
    if (duplicateNames.length > 0) {
      newErrors.general = 'Player names must be unique';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validatePlayers()) return;
    
    const playerRequests: CreatePlayerRequest[] = players.map(p => ({
      playerName: p.playerName.trim(),
      handicap: p.handicap,
      teamAssignment: p.teamAssignment,
      matchNumber: p.matchNumber // ✅ Include match number from CSV
    }));
    
    onPlayersSetup(playerRequests);
  };

  // CSV Import functionality
  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      if (!csvText) return;

      try {
        const lines = csvText.split('\n');
        const newPlayers: PlayerInput[] = [];
        
        // Skip header row, process data rows
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          const [name, handicapStr, team, matchStr] = line.split(',').map(s => s.trim());
          if (!name) continue;
          
          const handicap = handicapStr ? parseInt(handicapStr) : 0;
          const teamAssignment = (team?.toUpperCase() === 'B' || team?.toUpperCase() === 'TEAM B') ? 'B' : 'A';
          const matchNumber = matchStr ? parseInt(matchStr) : undefined;
          
          newPlayers.push({
            id: `imported-${Date.now()}-${Math.random()}`, // Generate unique ID
            playerName: name,
            handicap: isNaN(handicap) ? 0 : handicap,
            teamAssignment: format === 'singles' ? 'A' : teamAssignment,
            matchNumber: format === 'singles' ? undefined : (isNaN(matchNumber!) ? undefined : matchNumber)
          });
        }
        
        if (newPlayers.length > 0) {
          setPlayers([...players, ...newPlayers]);
          // Clear the file input
          event.target.value = '';
        }
      } catch (error) {
        alert('Error parsing CSV file. Please check the format.');
      }
    };
    
    reader.readAsText(file);
  };

  // Removed shuffle functionality - admin controls team assignments

  const getTeamPlayers = (team: 'A' | 'B') => {
    return players.filter(p => p.teamAssignment === team);
  };

  const renderSinglesLayout = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Players</h3>
        <button
          onClick={addPlayer}
          className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          Add Player
        </button>
      </div>
      
      <div className="grid gap-4">
        {players.map((player, index) => (
          <div key={player.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full">
              <User className="w-4 h-4 text-gray-600" />
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Player name"
                  value={player.playerName}
                  onChange={(e) => updatePlayer(player.id, 'playerName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors[`${player.id}-playerName`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors[`${player.id}-playerName`] && (
                  <p className="text-red-500 text-xs mt-1">{errors[`${player.id}-playerName`]}</p>
                )}
              </div>
              
              <div>
                <input
                  type="number"
                  placeholder="Handicap"
                  min="0"
                  max="54"
                  step="0.1"
                  value={player.handicap}
                  onChange={(e) => updatePlayer(player.id, 'handicap', parseFloat(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors[`${player.id}-handicap`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors[`${player.id}-handicap`] && (
                  <p className="text-red-500 text-xs mt-1">{errors[`${player.id}-handicap`]}</p>
                )}
              </div>
            </div>
            
            {players.length > 2 && (
              <button
                onClick={() => removePlayer(player.id)}
                className="p-2 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTeamLayout = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Team Setup</h3>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileImport}
            className="hidden"
            id="csv-import"
          />
          <label
            htmlFor="csv-import"
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 cursor-pointer"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </label>
          <button
            onClick={addPlayer}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Player
          </button>
        </div>
      </div>

      {/* CSV Format Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">CSV Import Format</h4>
            {format === 'singles' ? (
              <>
                <p className="text-sm text-blue-700 mb-2">
                  Format: <code className="bg-blue-100 px-1 rounded">Name, Handicap</code>
                </p>
                <p className="text-xs text-blue-600">
                  Example: John Doe, 10 | Jane Smith, 15
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-blue-700 mb-2">
                  Format: <code className="bg-blue-100 px-1 rounded">Name, Handicap, Team, Match</code>
                </p>
                <div className="text-xs text-blue-600 space-y-1">
                  <p className="font-medium">Example for {format}:</p>
                  <div className="bg-blue-100 p-2 rounded font-mono text-xs">
                    John Doe, 10, A, 1<br/>
                    Jane Smith, 15, A, 1<br/>
                    Bob Wilson, 8, B, 1<br/>
                    Mary Johnson, 12, B, 1<br/>
                    Mike Brown, 14, A, 2<br/>
                    Sue Davis, 9, A, 2
                  </div>
                  <p className="mt-2">Match number groups players into specific matches.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Team A */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">A</span>
            </div>
            <h4 className="text-lg font-semibold">Team A</h4>
            <span className="text-gray-500">({getTeamPlayers('A').length} players)</span>
          </div>
          
          <div className="space-y-3">
            {getTeamPlayers('A').map((player) => (
              <div key={player.id} className="flex items-center gap-3">
                <div className={`flex-1 grid gap-2 ${format !== 'singles' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <input
                    type="text"
                    placeholder="Player name"
                    value={player.playerName}
                    onChange={(e) => updatePlayer(player.id, 'playerName', e.target.value)}
                    className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors[`${player.id}-playerName`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <input
                    type="number"
                    placeholder="HC"
                    min="0"
                    max="54"
                    step="0.1"
                    value={player.handicap}
                    onChange={(e) => updatePlayer(player.id, 'handicap', parseFloat(e.target.value) || 0)}
                    className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors[`${player.id}-handicap`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {format !== 'singles' && (
                    <input
                      type="number"
                      placeholder="Match #"
                      min="1"
                      max="99"
                      value={player.matchNumber || ''}
                      onChange={(e) => updatePlayer(player.id, 'matchNumber', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  )}
                </div>
                <button
                  onClick={() => updatePlayer(player.id, 'teamAssignment', 'B')}
                  className="p-1 text-gray-400 hover:text-blue-500"
                  title="Move to Team B"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removePlayer(player.id)}
                  className="p-1 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Team B */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">B</span>
            </div>
            <h4 className="text-lg font-semibold">Team B</h4>
            <span className="text-gray-500">({getTeamPlayers('B').length} players)</span>
          </div>
          
          <div className="space-y-3">
            {getTeamPlayers('B').map((player) => (
              <div key={player.id} className="flex items-center gap-3">
                <div className={`flex-1 grid gap-2 ${format !== 'singles' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <input
                    type="text"
                    placeholder="Player name"
                    value={player.playerName}
                    onChange={(e) => updatePlayer(player.id, 'playerName', e.target.value)}
                    className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors[`${player.id}-playerName`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <input
                    type="number"
                    placeholder="HC"
                    min="0"
                    max="54"
                    step="0.1"
                    value={player.handicap}
                    onChange={(e) => updatePlayer(player.id, 'handicap', parseFloat(e.target.value) || 0)}
                    className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors[`${player.id}-handicap`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {format !== 'singles' && (
                    <input
                      type="number"
                      placeholder="Match #"
                      min="1"
                      max="99"
                      value={player.matchNumber || ''}
                      onChange={(e) => updatePlayer(player.id, 'matchNumber', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  )}
                </div>
                <button
                  onClick={() => updatePlayer(player.id, 'teamAssignment', 'A')}
                  className="p-1 text-gray-400 hover:text-red-500"
                  title="Move to Team A"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removePlayer(player.id)}
                  className="p-1 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Player Setup</h2>
        <p className="text-gray-600">
          Add players for your {format} tournament
          {format !== 'singles' && ' and assign them to teams'}
        </p>
      </div>

      {/* Format info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          {format === 'singles' ? <User className="w-5 h-5 text-blue-600" /> : <Users className="w-5 h-5 text-blue-600" />}
          <h3 className="font-semibold text-blue-900 capitalize">{format} Format</h3>
        </div>
        <p className="text-blue-800 text-sm">
          {format === 'singles' && 'Each player competes individually. Add pairs of players to create matches.'}
          {format === 'fourball' && 'Teams of 2 players each. Both players play their own ball, lowest score counts.'}
          {format === 'foursomes' && 'Teams of 2 players each. Players alternate shots with one ball per team.'}
        </p>
      </div>

      {/* Player input */}
      {format === 'singles' ? renderSinglesLayout() : renderTeamLayout()}

      {/* Errors */}
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{errors.general}</p>
        </div>
      )}

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>Total players: {players.length}</p>
          {format !== 'singles' && (
            <>
              <p>Team A: {getTeamPlayers('A').length} players</p>
              <p>Team B: {getTeamPlayers('B').length} players</p>
              <p>Potential matches: {Math.floor(Math.min(getTeamPlayers('A').length, getTeamPlayers('B').length) / 2)}</p>
            </>
          )}
          {format === 'singles' && (
            <p>Potential matches: {Math.floor(players.length / 2)}</p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between pt-4">
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading || players.length === 0}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Adding Players...' : 'Continue to Match Generation'}
        </button>
      </div>
    </div>
  );
} 